import { useCallback, useEffect, useState } from "react";
import { CreditCard, Loader2, RefreshCcw, ShieldCheck } from "lucide-react";
import { toast } from "react-toastify";
import { commercePaymentsApi } from "../../api/commercePaymentsApi";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import { commerceSettingsStyles as styles } from "./commerceSettingsStyles";
import axiosClient from "../../api/axiosClient";
import AutoReplyFollowUpEditor, {
  emptyFollowUp,
  validateFollowUp,
} from "../AutoReplyBuilder/components/AutoReplyFollowUpEditor";

function readError(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

const COLLECTION_MODES = [
  {
    value: "manual",
    label: "Handle payments manually",
    description: "Use XploreByte to manage your orders while collecting payment yourself.",
  },
  {
    value: "afterAcceptance",
    label: "Request payment after accepting an order",
    description: "Review each order first, then send the customer a payment request.",
    badge: "Default",
  },
  {
    value: "immediateAfterCheckout",
    label: "Request payment immediately after checkout",
    description: "Customers receive a payment request as soon as checkout is completed.",
  },
];

const COLLECTION_MODE_ALIASES = {
  manual: "manual",
  Manual: "manual",
  afterAcceptance: "afterAcceptance",
  AfterAcceptance: "afterAcceptance",
  immediateAfterCheckout: "immediateAfterCheckout",
  ImmediateAfterCheckout: "immediateAfterCheckout",
};

export const normalizePaymentCollectionMode = value =>
  COLLECTION_MODE_ALIASES[value] || "afterAcceptance";

const emptyNativeDraft = wabaId => ({
  id: null,
  wabaId: wabaId || "",
  configurationName: "",
  provider: "razorpay",
  providerMid: "",
  metaStatus: "needsConnecting",
  isEnabled: false,
  expectedVersion: 0,
});

const statusLabel = value => ({
  active: "Active",
  needsConnecting: "Needs Connecting",
  needsTesting: "Needs Testing",
}[value] || value);

const safeAccount = value => value && value.length > 8
  ? `${value.slice(0, 4)}…${value.slice(-4)}`
  : value;

function ModeOption({ option, selected, disabled, onChange }) {
  return (
    <label className={`${styles.optionRow} border-b border-slate-100 last:border-b-0 ${disabled ? "cursor-not-allowed opacity-70" : ""} ${selected ? styles.optionRowSelected : ""}`}>
      <input
        type="radio"
        name="payment-collection-mode"
        value={option.value}
        aria-label={option.label}
        className={styles.radio}
        checked={selected}
        disabled={disabled}
        onChange={() => onChange(option.value)}
      />
      <span>
        <span className="flex items-center gap-2 text-sm font-medium text-slate-900">
          {option.label}
          {option.badge ? <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-500">{option.badge}</span> : null}
        </span>
        <span className="mt-1 block text-xs leading-5 text-slate-500">{option.description}</span>
      </span>
    </label>
  );
}

export default function OrderPaymentsPage() {
  const { can, businessId } = useAuth();
  const canConfigure = can(FK.COMMERCE_PAYMENT_CONFIGURE);
  const [configuration, setConfiguration] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savingNative, setSavingNative] = useState(false);
  const [nativeDraft, setNativeDraft] = useState(emptyNativeDraft());
  const [followUp, setFollowUp] = useState(emptyFollowUp());
  const [savedFollowUp, setSavedFollowUp] = useState(emptyFollowUp());
  const [templates, setTemplates] = useState([]);
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [value, followUpValue] = await Promise.all([
        commercePaymentsApi.getConfiguration(),
        typeof commercePaymentsApi.getFollowUp === "function"
          ? commercePaymentsApi.getFollowUp()
          : Promise.resolve({ enabled: false, steps: [] }),
      ]);
      setConfiguration(value);
      setDraft({
        paymentCollectionMode: normalizePaymentCollectionMode(value.paymentCollectionMode),
        cashOnDeliveryEnabled: value.cashOnDeliveryEnabled,
        payAtPickupEnabled: value.payAtPickupEnabled,
      });
      const native = value.nativeWhatsAppConfigurations?.[0];
      setNativeDraft(native ? {
        id: native.id,
        wabaId: native.wabaId,
        configurationName: native.configurationName,
        provider: "razorpay",
        providerMid: native.providerMid,
        metaStatus: native.metaStatus,
        isEnabled: native.isEnabled,
        expectedVersion: native.version,
      } : emptyNativeDraft(value.nativeWhatsAppWabas?.[0]?.wabaId));
      const normalizedFollowUp = {
        enabled: followUpValue?.enabled === true,
        steps: Array.isArray(followUpValue?.steps) ? followUpValue.steps : [],
      };
      setFollowUp(normalizedFollowUp);
      setSavedFollowUp(normalizedFollowUp);
    } catch (requestError) {
      setError(readError(requestError, "Order Payment settings could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    if (!businessId) return undefined;
    axiosClient.get(`templates/${businessId}`, {
      params: { status: "APPROVED", page: 1, pageSize: 200, sortKey: "name", sortDir: "asc" },
    }).then(({ data }) => {
      if (!active) return;
      const rows = Array.isArray(data?.templates) ? data.templates : [];
      setTemplates(rows.map(item => ({
        id: item.id || item.Id,
        name: item.name || item.Name,
        languageCode: item.languageCode || item.LanguageCode || item.language || item.Language || "",
      })).filter(item => item.name));
    }).catch(() => active && setTemplates([]));
    return () => { active = false; };
  }, [businessId]);

  async function saveFollowUp() {
    if (savingFollowUp || !canConfigure) return;
    const validation = validateFollowUp(followUp, 0);
    if (validation) return toast.error(validation);
    setSavingFollowUp(true);
    try {
      const value = await commercePaymentsApi.updateFollowUp(followUp);
      const normalized = {
        enabled: value?.enabled === true,
        steps: Array.isArray(value?.steps) ? value.steps : [],
      };
      setFollowUp(normalized);
      setSavedFollowUp(normalized);
      toast.success("Payment follow-ups saved.");
    } catch (requestError) {
      toast.error(readError(requestError, "Payment follow-ups could not be saved."));
    } finally {
      setSavingFollowUp(false);
    }
  }

  async function save() {
    if (saving || !configuration || !draft) return;
    setSaving(true);
    try {
      const value = await commercePaymentsApi.updateSettings({
        ...draft,
        expectedVersion: configuration.version,
      });
      setConfiguration(value);
      setDraft({
        paymentCollectionMode: normalizePaymentCollectionMode(value.paymentCollectionMode),
        cashOnDeliveryEnabled: value.cashOnDeliveryEnabled,
        payAtPickupEnabled: value.payAtPickupEnabled,
      });
      toast.success("Order Payment settings saved.");
    } catch (requestError) {
      toast.error(readError(requestError, "Order Payment settings could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  async function connectRazorpay() {
    if (connecting || !canConfigure) return;
    setConnecting(true);
    try {
      const result = await commercePaymentsApi.startRazorpayAuthorization();
      window.location.assign(result.authorizationUrl);
    } catch (requestError) {
      toast.error(readError(requestError, "Razorpay authorization could not be started."));
      setConnecting(false);
    }
  }

  async function saveNativeConfiguration() {
    if (savingNative || !canConfigure) return;
    setSavingNative(true);
    try {
      const value = await commercePaymentsApi.upsertNativeConfiguration(nativeDraft);
      setConfiguration(value);
      const saved = value.nativeWhatsAppConfigurations?.find(
        item => item.wabaId === nativeDraft.wabaId &&
          item.configurationName === nativeDraft.configurationName,
      );
      if (saved) setNativeDraft({
        id: saved.id,
        wabaId: saved.wabaId,
        configurationName: saved.configurationName,
        provider: "razorpay",
        providerMid: saved.providerMid,
        metaStatus: saved.metaStatus,
        isEnabled: saved.isEnabled,
        expectedVersion: saved.version,
      });
      toast.success("Native WhatsApp payment configuration saved.");
    } catch (requestError) {
      toast.error(readError(requestError,
        "Native WhatsApp payment configuration could not be saved."));
    } finally {
      setSavingNative(false);
    }
  }

  if (loading) {
    return <div role="status" className="flex min-h-64 items-center justify-center text-sm text-slate-500"><Loader2 size={18} className="mr-2 animate-spin" /> Loading Order Payments</div>;
  }

  if (error) {
    return <main className="mx-auto max-w-4xl px-4 py-6"><div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"><p>{error}</p><button type="button" onClick={load} className={`${styles.secondaryButton} mt-3`}><RefreshCcw size={15} /> Retry</button></div></main>;
  }

  const razorpay = configuration.razorpay;
  const changed = draft.paymentCollectionMode !==
      normalizePaymentCollectionMode(configuration.paymentCollectionMode) ||
    draft.cashOnDeliveryEnabled !== configuration.cashOnDeliveryEnabled ||
    draft.payAtPickupEnabled !== configuration.payAtPickupEnabled;
  const followUpChanged = JSON.stringify(followUp) !== JSON.stringify(savedFollowUp);

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <header className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700"><CreditCard size={20} /></div>
        <div><h1 className="text-xl font-semibold text-slate-900">Order Payments</h1><p className="mt-1 text-sm text-slate-600">Choose when XploreByte should collect payment for Commerce Orders.</p></div>
      </header>

      <div className="grid gap-5">
        <section className={styles.card} aria-label="Payment pending follow-ups">
          <header className="border-b border-slate-200 px-5 py-3">
            <h2 className="text-sm font-semibold text-slate-900">Payment pending follow-ups</h2>
          </header>
          <div className="p-5">
            <AutoReplyFollowUpEditor
              value={followUp}
              onChange={setFollowUp}
              templates={templates}
              description="Send reminders only while the payment provider confirms payment is still outstanding."
            />
          </div>
          {canConfigure ? <footer className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
            <button type="button" onClick={saveFollowUp}
              disabled={!followUpChanged || savingFollowUp || Boolean(validateFollowUp(followUp, 0))}
              className={styles.primaryButton}>
              {savingFollowUp ? <Loader2 size={16} className="animate-spin" /> : null} Save follow-ups
            </button>
          </footer> : null}
        </section>
        <section className={styles.card}>
          <header className="border-b border-slate-200 px-5 py-3"><h2 className="text-sm font-semibold text-slate-900">Payment collection</h2></header>
          {COLLECTION_MODES.map(option => (
            <ModeOption
              key={option.value}
              option={option}
              selected={draft.paymentCollectionMode === option.value}
              disabled={!canConfigure || saving}
              onChange={value => setDraft(current => ({ ...current, paymentCollectionMode: value }))}
            />
          ))}
          {draft.paymentCollectionMode === "immediateAfterCheckout" ? <p className="border-t border-amber-100 bg-amber-50 px-5 py-3 text-xs leading-5 text-amber-900">If you later cancel or reject a paid order, refunds currently need to be processed manually through Razorpay.</p> : null}
          {canConfigure ? <footer className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3"><button type="button" onClick={save} disabled={!changed || saving} className={styles.primaryButton}>{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save</button></footer> : null}
        </section>

        <section className={styles.card}>
          <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3"><h2 className="text-sm font-semibold text-slate-900">Online payments</h2><span className={razorpay.isConnected ? "text-xs font-medium text-emerald-700" : "text-xs font-medium text-slate-500"}>{razorpay.isConnected ? "Connected" : "Not connected"}</span></header>
          <div className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div><div className="flex items-center gap-2"><ShieldCheck size={18} className="text-slate-500" /><p className="font-medium text-slate-900">Razorpay</p></div><p className="mt-2 text-sm text-slate-600">Payments settle directly to your connected Razorpay account.</p>{razorpay.providerAccountId ? <p className="mt-2 text-xs text-slate-500">Account: {razorpay.providerAccountId}</p> : null}</div>
            <button type="button" onClick={connectRazorpay} disabled={!canConfigure || connecting} className={styles.primaryButton}>{connecting ? <Loader2 size={16} className="animate-spin" /> : null}{razorpay.isConnected ? "Reconnect" : "Connect Razorpay"}</button>
          </div>
          <div className="grid border-t border-slate-100 bg-slate-50 text-xs text-slate-600 sm:grid-cols-2">
            <p className="px-5 py-3">Payment Links: <span>{razorpay.paymentLinksReady ? "Ready" : "Not ready"}</span></p>
            <p className="px-5 py-3">Provider account: <span>{safeAccount(razorpay.providerAccountId) || "Unavailable"}</span></p>
          </div>
        </section>

        <section className={styles.card} aria-label="Native WhatsApp Payments">
          <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <div><h2 className="text-sm font-semibold text-slate-900">Native WhatsApp Payments</h2><p className="mt-1 text-xs text-slate-500">WABA-scoped Meta Direct Pay configuration. Meta synchronization is not automatic.</p><p className="mt-1 text-xs text-slate-600">Native WhatsApp payments use your live Razorpay account. Connect Razorpay in Live mode to accept native payments.</p></div>
            <span className={razorpay.nativePaymentsReady ? "text-xs font-medium text-emerald-700" : "text-xs font-medium text-amber-700"}>{razorpay.nativePaymentsReady ? "Ready" : "Not ready"}</span>
          </header>
          {(configuration.nativeWhatsAppConfigurations || []).map(item => (
            <div key={item.id} className="border-b border-slate-100 px-5 py-4 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-medium text-slate-900">{item.configurationName}</p><span className="text-xs text-slate-500">{statusLabel(item.metaStatus)}</span></div>
              <p className="mt-1 text-xs text-slate-500">WABA {item.wabaId} · Razorpay MID {safeAccount(item.providerMid)}</p>
              <p className={`mt-2 text-xs ${item.isReady ? "text-emerald-700" : "text-amber-700"}`}>{item.isReady ? "Ready for this environment" : item.readinessMessage || "Not ready"}</p>
              {item.phones?.length ? <p className="mt-1 text-xs text-slate-500">Phone: {item.phones.map(phone => phone.displayPhoneNumber || phone.phoneNumberId).join(", ")}</p> : null}
              {canConfigure ? <button type="button" className="mt-3 text-xs font-medium text-emerald-700" onClick={() => setNativeDraft({ id: item.id, wabaId: item.wabaId, configurationName: item.configurationName, provider: "razorpay", providerMid: item.providerMid, metaStatus: item.metaStatus, isEnabled: item.isEnabled, expectedVersion: item.version })}>Edit local configuration</button> : null}
            </div>
          ))}
          {canConfigure && configuration.nativeWhatsAppWabas?.length ? <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-700">WABA<select aria-label="Native payment WABA" className={styles.field} value={nativeDraft.wabaId} disabled={Boolean(nativeDraft.id)} onChange={event => setNativeDraft(current => ({ ...current, wabaId: event.target.value }))}>{configuration.nativeWhatsAppWabas.map(waba => <option key={waba.wabaId} value={waba.wabaId}>{waba.wabaId}</option>)}</select></label>
            <label className="text-xs font-medium text-slate-700">Configuration name<input aria-label="Native payment configuration name" className={styles.field} value={nativeDraft.configurationName} onChange={event => setNativeDraft(current => ({ ...current, configurationName: event.target.value }))} /></label>
            <label className="text-xs font-medium text-slate-700">Provider<input className={styles.field} value="Razorpay" disabled readOnly /></label>
            <label className="text-xs font-medium text-slate-700">Razorpay provider MID<input aria-label="Native payment provider MID" className={styles.field} value={nativeDraft.providerMid} onChange={event => setNativeDraft(current => ({ ...current, providerMid: event.target.value }))} /></label>
            <label className="text-xs font-medium text-slate-700">Meta status<select aria-label="Native payment Meta status" className={styles.field} value={nativeDraft.metaStatus} onChange={event => setNativeDraft(current => ({ ...current, metaStatus: event.target.value }))}><option value="needsConnecting">Needs Connecting</option><option value="needsTesting">Needs Testing</option><option value="active">Active</option></select></label>
            <label className="flex items-center gap-3 text-sm text-slate-700"><input aria-label="Enable native payment configuration" type="checkbox" className={styles.checkbox} checked={nativeDraft.isEnabled} onChange={event => setNativeDraft(current => ({ ...current, isEnabled: event.target.checked }))} />Use this configuration for its WABA</label>
            <div className="sm:col-span-2 flex justify-end"><button type="button" className={styles.primaryButton} disabled={savingNative || !nativeDraft.wabaId || !nativeDraft.configurationName.trim() || !nativeDraft.providerMid.trim()} onClick={saveNativeConfiguration}>{savingNative ? <Loader2 size={16} className="animate-spin" /> : null}Save native configuration</button></div>
          </div> : null}
          {!configuration.nativeWhatsAppWabas?.length ? <p className="px-5 py-4 text-sm text-amber-800">Connect an active Meta WABA and phone number before configuring Native WhatsApp Payments.</p> : null}
        </section>
      </div>
    </main>
  );
}
